/**
 * Página de Lista de Cotizaciones
 *
 * Lista de cotizaciones con filtros por estado y cliente
 * Incluye badge de revisión y navegación a detalle
 *
 * @module pages/Cotizaciones
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/contexts/AuthContext';
import { useToast } from '../components/contexts/ToastContext';
import Navbar from '../components/layout/Navbar';
import usePaginatedEntity from '../hooks/usePaginatedEntity';
import EntityTable from '../components/common/EntityTable';
import HighlightedText from '../components/common/HighlightedText';
import EntityModal from '../components/common/EntityModal';
import FilterPanel from '../components/common/FilterPanel';
import Pagination from '../components/common/Pagination';
import ConfirmDialog from '../components/common/ConfirmDialog';
import apiService from '../services/apiService';
import { validateDate, validateDateRange } from '../utils/sanitization';
import { ESTADOS_COTIZACION } from '../utils/cotizacionHelpers';
import { useExportExcel } from '../hooks/useExportExcel';
import { renderActionButton } from '../components/common/EntityTable';
import Breadcrumb from '../components/common/Breadcrumb';
import ROUTES from '../constants/routes';

/**
 * Renderiza el badge de revisión
 *
 * @param {number} revision - Número de revisión
 * @returns {JSX.Element}
 */
const renderRevisionBadge = (revision) => {
  const badgeClass = revision === 0 ? 'badge-secondary' : 'badge-info';
  return (
    <span className={`badge ${badgeClass}`}>
      Rev. {revision}
    </span>
  );
};

/**
 * Componente principal de lista de cotizaciones
 *
 * @returns {JSX.Element}
 */
function Cotizaciones() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, cotizacion: null, loading: false });

  const {
    data,
    rawData,
    loading,
    pagination,
    filters,
    modalOpen,
    editingItem,
    confirmDialog,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    handleCreate,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleSave,
    closeModal,
    closeConfirm,
    refetch
  } = usePaginatedEntity({
    entityName: 'cotización',
    entityNamePlural: 'cotizaciones',
    fetchFn: apiService.getCotizaciones,
    createFn: apiService.createCotizacion,
    updateFn: apiService.updateCotizacion,
    deleteFn: apiService.deleteCotizacion,
    filterKeys: ['search', 'estado'],
    messages: {
      createSuccess: 'Cotización creada',
      updateSuccess: 'Cotización actualizada',
      deleteSuccess: 'Cotización eliminada exitosamente'
    }
  });

  // Cargar lista de clientes para el selector
  useEffect(() => {
    const fetchClientes = async () => {
      setLoadingClientes(true);
      try {
        const response = await apiService.getClientes({ limit: 1000 });
        if (response.success) {
          setClientes(response.data);
        }
      } catch (err) {
        console.error('Error cargando clientes:', err);
      } finally {
        setLoadingClientes(false);
      }
    };

    if (modalOpen) {
      fetchClientes();
    }
  }, [modalOpen]);

  /**
   * Abre el modal de confirmación de duplicación
   *
   * @param {Object} cotizacion - Cotización a duplicar
   */
  const handleDuplicar = (cotizacion) => {
    setDuplicateDialog({ open: true, cotizacion, loading: false });
  };

  /**
   * Confirma y ejecuta la duplicación de la cotización
   */
  const confirmDuplicate = async () => {
    setDuplicateDialog(prev => ({ ...prev, loading: true }));
    try {
      const res = await apiService.duplicarCotizacion(duplicateDialog.cotizacion.id);
      if (res.success) {
        setDuplicateDialog({ open: false, cotizacion: null, loading: false });
        showToast(res.message || 'Cotización duplicada exitosamente', 'success');
        refetch();
      } else {
        setDuplicateDialog(prev => ({ ...prev, loading: false }));
      }
    } catch (err) {
      setDuplicateDialog(prev => ({ ...prev, loading: false }));
      showToast(err.message || 'Error al duplicar cotización', 'error');
    }
  };

  /**
   * Cierra el modal de duplicación sin realizar cambios
   */
  const closeDuplicate = () => {
    setDuplicateDialog({ open: false, cotizacion: null, loading: false });
  };

  /**
   * Navega al detalle de una cotización
   *
   * @param {Object} cotizacion - Cotización a ver
   */
  const handleVerDetalle = (cotizacion) => {
    navigate(`/cotizaciones/${cotizacion.id}`);
  };

  const actions = [
    { icon: 'fa-eye',   className: 'btn-ghost-info',    title: 'Ver detalle',                            label: 'Ver',      onClick: handleVerDetalle, show: () => hasPermission('cotizaciones.read') },
    { icon: 'fa-copy',  className: 'btn-ghost-warning',  title: 'Duplicar como nueva cotización (Rev 0)', label: 'Duplicar', onClick: handleDuplicar,   show: () => hasPermission('cotizaciones.create') },
    { icon: 'fa-edit',  className: 'btn-ghost-primary',  title: 'Editar',                                 label: 'Editar',   onClick: handleEdit,       show: () => hasPermission('cotizaciones.update') },
    { icon: 'fa-trash', className: 'btn-ghost-danger',   title: 'Eliminar',                                                  onClick: handleDelete,     show: () => hasPermission('cotizaciones.delete') },
  ];

  const exportToExcel = useExportExcel({
    data: rawData,
    mapFn: (c) => ({
      'Número':        c.numero,
      'Revisión':      c.revision,
      'Cliente':       c.cliente?.razon_social || '',
      'RUT':           c.cliente?.rut || '',
      'Fecha Emisión': c.fecha_emision ? new Date(c.fecha_emision).toLocaleDateString('es-CL') : '',
      'Estado':        ESTADOS_COTIZACION[c.estado]?.label || c.estado || '',
      'Total':         parseFloat(c.total || 0),
      'Total Ítems':   c.total_items || 0,
    }),
    sheetName: 'Cotizaciones',
    columnWidths: [
      { wch: 14 }, { wch: 8 }, { wch: 30 }, { wch: 14 },
      { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 12 }
    ],
    entityLabel: 'cotizaciones'
  });

  // Configuración de columnas para la tabla
  const columns = useMemo(() => [
    {
      key: 'numero',
      label: 'Número',
      sortable: true,
      title: true,
      render: (row, searchTerm) => (
        <strong><HighlightedText text={row.numero || ''} searchTerm={searchTerm} /></strong>
      )
    },
    {
      key: 'revision',
      label: 'Revisión',
      sortable: true,
      align: 'center',
      mobileHidden: true,
      render: (row) => renderRevisionBadge(row.revision || 0)
    },
    {
      key: 'cliente',
      label: 'Cliente',
      render: (row, searchTerm) => (
        <div>
          <strong><HighlightedText text={row.cliente?.razon_social || ''} searchTerm={searchTerm} /></strong>
          <div className="text-muted">
            <HighlightedText text={row.cliente?.rut || ''} searchTerm={searchTerm} />
          </div>
        </div>
      )
    },
    {
      key: 'fecha_emision',
      label: 'Fecha Emisión',
      sortable: true,
      render: (row) => row.fecha_emision
        ? new Date(row.fecha_emision).toLocaleDateString('es-CL')
        : '-'
    },
    {
      key: 'estado',
      label: 'Estado',
      sortable: true,
      mobileBadge: true,
      render: (row, searchTerm) => {
        const cfg = ESTADOS_COTIZACION[row.estado];
        return (
          <span className={`badge ${cfg?.badge || ''}`}>
            <HighlightedText text={cfg?.label || row.estado || ''} searchTerm={searchTerm} />
          </span>
        );
      }
    },
    {
      key: 'total',
      label: 'Total',
      sortable: true,
      render: (row) => (
        <strong>${parseFloat(row.total || 0).toLocaleString('es-CL')}</strong>
      )
    },
    {
      key: 'total_items',
      label: 'Items',
      mobileHidden: true,
      render: (row) => row.total_items || 0
    },
    { key: 'acciones', label: 'Acciones', align: 'center' }
  ], []);

  // Configuración de campos para el modal de creación
  const modalFields = useMemo(() => [
    {
      name: 'cliente_id',
      label: 'Cliente',
      type: 'select',
      required: true,
      options: clientes.map(c => ({
        value: c.id,
        label: `${c.razon_social} (${c.rut})`
      })),
      disabled: loadingClientes
    },
    {
      name: 'fecha_emision',
      label: 'Fecha Emisión',
      type: 'date',
      required: true,
      validators: [(value) => validateDate(value, 'Fecha de emisión')]
    },
    {
      name: 'fecha_vencimiento',
      label: 'Fecha Vencimiento',
      type: 'date',
      validators: [(value) => {
        if (!value) return { valid: true, message: '' }; // Opcional
        return validateDate(value, 'Fecha de vencimiento');
      }]
    },
    {
      name: 'fecha_inicio_arriendo',
      label: 'Inicio Arriendo (estimado)',
      type: 'date',
      validators: [(value) => {
        if (!value) return { valid: true, message: '' };
        return validateDate(value, 'Fecha de inicio de arriendo');
      }]
    },
    {
      name: 'fecha_fin_arriendo',
      label: 'Fin Arriendo (estimado)',
      type: 'date',
      validators: [(value) => {
        if (!value) return { valid: true, message: '' };
        return validateDate(value, 'Fecha de fin de arriendo');
      }]
    },
    {
      name: 'descripcion',
      label: 'Descripción',
      type: 'textarea',
      placeholder: 'Descripción general del servicio'
    }
  ], [clientes, loadingClientes]);

  // Configuración de filtros
  const filterConfig = useMemo(() => [
    {
      name: 'search',
      type: 'text',
      placeholder: 'Buscar por número, descripción o cliente...',
      icon: 'fa-search'
    },
    {
      name: 'estado',
      type: 'select',
      label: 'Estado',
      options: [
        { value: '', label: 'Todos' },
        { value: 'borrador', label: 'Borrador' },
        { value: 'enviada', label: 'Enviada' },
        { value: 'aprobada', label: 'Aprobada' },
        { value: 'rechazada', label: 'Rechazada' },
        { value: 'vencida', label: 'Vencida' }
      ]
    }
  ], []);

  const renderCotizacionMobileCard = useCallback((row, index, visibleActions) => {
    const cfg = ESTADOS_COTIZACION[row.estado];
    return (
      <div key={row.id || index} className={`mobile-card status-${row.estado}`}>
        <div className="mobile-card-header">
          <span className="mobile-card-title">
            <HighlightedText text={row.numero || ''} searchTerm={filters.search} />
          </span>
          <span className={`badge ${cfg?.badge || ''}`}>{cfg?.label || row.estado || ''}</span>
        </div>
        <div className="mobile-card-body">
          <div className="mobile-card-field">
            <span className="mobile-card-label">Cliente</span>
            <span className="mobile-card-value">
              <HighlightedText text={row.cliente?.razon_social || '-'} searchTerm={filters.search} />
            </span>
          </div>
          <div className="mobile-card-field">
            <span className="mobile-card-label">Fecha Emisión</span>
            <span className="mobile-card-value">
              {row.fecha_emision ? new Date(row.fecha_emision).toLocaleDateString('es-CL') : '-'}
            </span>
          </div>
          <div className="mobile-card-field">
            <span className="mobile-card-label">Total</span>
            <span className="mobile-card-value">
              <strong>${parseFloat(row.total || 0).toLocaleString('es-CL')}</strong>
            </span>
          </div>
        </div>
        {visibleActions?.length > 0 && (
          <div className="card-actions">
            {visibleActions.map(action =>
              renderActionButton(action, row, action.className?.includes('danger') ? 'btn-table-action' : 'btn-sm')
            )}
          </div>
        )}
      </div>
    );
  }, [filters.search]);

  /**
   * Maneja el guardado exitoso de una cotización
   * Navega al detalle después de crear
   *
   * @param {Object} data - Datos guardados
   */
  const handleSaveSuccess = async (data) => {
    await handleSave(data);
    // Si es creación exitosa, navegar al detalle
    if (!editingItem) {
      // Refrescar para obtener el ID de la cotización creada
      await refetch();
      // Navegar al detalle (el ID vendrá en la respuesta)
      // Por ahora solo refrescamos, el usuario puede hacer clic en "Ver detalle"
    }
  };

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Cotizaciones' }
        ]} />

        <FilterPanel
          filters={filters}
          filterConfig={filterConfig}
          onFilterChange={handleFilterChange}
          actionConfig={[
            { label: 'Exportar Excel',    icon: 'fa-file-excel', variant: 'secondary', onClick: exportToExcel },
            { label: 'Nueva Cotización',  icon: 'fa-plus',       variant: 'primary',   onClick: handleCreate, show: () => hasPermission('cotizaciones.create') }
          ]}
        />

        <EntityTable
          columns={columns}
          data={data}
          loading={loading}
          searchTerm={filters.search}
          emptyMessage="No hay cotizaciones registradas"
          animateRows={true}
          actions={actions}
          getRowClassName={(row) => `status-${row.estado}`}
          mobileCardRenderer={renderCotizacionMobileCard}
        />

        <Pagination
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleLimitChange}
          loading={loading}
        />

        <EntityModal
          isOpen={modalOpen}
          onClose={closeModal}
          onSave={handleSaveSuccess}
          title={editingItem ? 'Editar Cotización' : 'Nueva Cotización'}
          fields={modalFields}
          initialData={editingItem}
          crossFieldValidator={(formData) => {
            const errors = {};

            // Validar vencimiento > emisión
            if (formData.fecha_emision && formData.fecha_vencimiento) {
              const result = validateDateRange(
                formData.fecha_emision,
                formData.fecha_vencimiento,
                'Fecha de emisión',
                'Fecha de vencimiento'
              );
              if (!result.valid) {
                errors.fecha_vencimiento = result.message;
              }
            }

            // Validar fin arriendo > inicio arriendo
            if (formData.fecha_inicio_arriendo && formData.fecha_fin_arriendo) {
              const result = validateDateRange(
                formData.fecha_inicio_arriendo,
                formData.fecha_fin_arriendo,
                'Fecha de inicio de arriendo',
                'Fecha de fin de arriendo'
              );
              if (!result.valid) {
                errors.fecha_fin_arriendo = result.message;
              }
            }

            return errors;
          }}
        />

        <ConfirmDialog
          isOpen={confirmDialog.open}
          onClose={closeConfirm}
          onConfirm={confirmDelete}
          title="Eliminar Cotización"
          message={`¿Está seguro de eliminar la cotización "${confirmDialog.item?.numero}"?`}
          confirmText="Eliminar"
          variant="danger"
        />

        <ConfirmDialog
          isOpen={duplicateDialog.open}
          onClose={closeDuplicate}
          onConfirm={confirmDuplicate}
          title="Duplicar Cotización"
          message={`¿Duplicar la cotización "${duplicateDialog.cotizacion?.numero}"? Se creará una nueva cotización independiente en Rev 0 y estado Borrador con los mismos ítems y notas.`}
          confirmText="Duplicar"
          variant="warning"
          loading={duplicateDialog.loading}
        />
      </div>
    </>
  );
}

export default Cotizaciones;
